import { GenericAbortSignal } from 'axios';

import { HighCostMedicationListItem, RxnormCoding, RxnormStateCounts } from 'models/HighCostMedicationListItem';
import { Paginated } from 'models/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export type HighCostMedicationListItemsIndexParams = BaseIndexQueryParams & { rxnormState?: string[] };

export const highCostMedicationListItemQueryKeys = {
  base: ['highCostMedicationListItems'],
  index: (params: Record<string, any> = {}) => ['highCostMedicationListItems', 'index', params],
};

// No list id in the path: the API resolves the acting client's active list through its policy scope,
// and a client with no list returns an empty page rather than an error. See design 003.
export async function indexHighCostMedicationListItems(
  params: HighCostMedicationListItemsIndexParams,
  signal: GenericAbortSignal
) {
  return http
    .get<
      Paginated<HighCostMedicationListItem, { rxnormStateCounts?: RxnormStateCounts }>
    >('/high_cost_medication_list_items', { params, signal })
    .then((response) => response.data);
}

// The six writable fields. genericName is the only one the API requires; brandName is a match key
// olio-ai compares against and the rest is reviewer context, so all five are nullable.
//
// costTier is the tier's free-text label rather than an id: the cost tier key is a rollup of what the
// medications carry, so sending a label the list has never seen creates the tier. There is no
// clientId, unlike the admin variant — the API resolves the acting client from the session.
export type HighCostMedicationListItemMutationParams = {
  genericName: string;
  brandName: string | null;
  costTier: string | null;
  dosage: string | null;
  route: string | null;
  form: string | null;
};

// The first medication a client adds provisions their list server-side; there is no create-a-list
// call to make first. See design 003.
export async function createHighCostMedicationListItem(params: HighCostMedicationListItemMutationParams) {
  return http
    .post<HighCostMedicationListItem>('/high_cost_medication_list_items', params)
    .then((response) => response.data);
}

export async function updateHighCostMedicationListItem({
  id,
  ...params
}: Partial<HighCostMedicationListItemMutationParams> & { id: string }) {
  return http
    .patch<HighCostMedicationListItem>(`/high_cost_medication_list_items/${id}`, params)
    .then((response) => response.data);
}

// Soft-archives the medication; nothing is hard-deleted, and it stops flagging on new referrals.
export async function destroyHighCostMedicationListItem({ id }: { id: string }) {
  return http.delete(`/high_cost_medication_list_items/${id}`);
}

// Codes the typed names without writing anything. When olio-ai cannot answer the api still returns
// 200, with state, generic and brand all null. See design 008, "The coding preview".
export async function previewHighCostMedicationListItemCoding(params: {
  genericName: string;
  brandName: string | null;
}) {
  return http
    .post<RxnormCoding>('/high_cost_medication_list_items/rxnorm_codings', params)
    .then((response) => response.data);
}
